// Utility helpers for validating and sanitizing human names.
// - Allows letters (including accented), spaces, hyphens (-) and apostrophes (')
// - Trims leading/trailing whitespace and collapses multiple internal spaces to single
// - Disallows digits, emojis, and punctuation other than ' and -
// - Exposes: sanitizeName, isValidName, validateName

// Note: This is intentionally permissive for international names but restrictive
// about digits, emojis and most punctuation. You can extend options if needed.

// Regex pieces:
// \p{L} matches any kind of letter from any language (requires unicode flag)
// Allowed characters: letters, space, hyphen, apostrophe
const NAME_ALLOWED_REGEX = /^[\p{L} '-]+$/u;

// A simple emoji detection using unicode ranges often used for emoji characters.
// This is best-effort; emoji regexes are large — we keep a compact check that
// catches the majority of common emoji code points.
const EMOJI_REGEX = /[\p{Emoji}\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;

/**
 * Trims whitespace and collapses multiple spaces into single spaces.
 * Also normalizes non-breaking spaces to regular spaces.
 */
function sanitizeName(input) {
  if (input === null || input === undefined) return "";
  // Convert to string
  let s = String(input);
  // Normalize Unicode to NFC so accents are consistent
  try { s = s.normalize('NFC'); } catch (e) { /* ignore if not supported */ }
  // Replace various whitespace (including NBSP) with normal space
  s = s.replace(/\s+/g, ' ');
  // Trim edges
  s = s.trim();
  return s;
}

/**
 * Checks if the name is valid according to rules.
 * Options:
 *  - minLength (default 1), maxLength (default 100)
 */
function isValidName(input, options = {}) {
  const { minLength = 1, maxLength = 50 } = options;
  if (input === null || input === undefined) return false;
  const s = sanitizeName(input);
  if (s.length < minLength || s.length > maxLength) return false;
  // Reject if contains emoji
  if (EMOJI_REGEX.test(s)) return false;
  // Must match allowed set: letters (any language), space, hyphen, apostrophe
  if (!NAME_ALLOWED_REGEX.test(s)) return false;
  // Also disallow names that are only punctuation like "-" or "'"
  const lettersOnly = s.replace(/[ '\-]/g, '');
  if (lettersOnly.length === 0) return false;
  return true;
}

/**
 * Returns a structured validation result { valid, sanitized, message }
 */
function validateName(input, options = {}) {
  const sanitized = sanitizeName(input);
  const { minLength = 1, maxLength = 50 } = options;
  if (!sanitized) {
    return { valid: false, sanitized, message: 'Name is required' };
  }
  if (sanitized.length < minLength) {
    return { valid: false, sanitized, message: `Name must be at least ${minLength} characters` };
  }
  if (sanitized.length > maxLength) {
    return { valid: false, sanitized, message: `Name must be at most ${maxLength} characters` };
  }
  if (EMOJI_REGEX.test(sanitized)) {
    return { valid: false, sanitized, message: 'Emojis are not allowed in name' };
  }
  if (!NAME_ALLOWED_REGEX.test(sanitized)) {
    return { valid: false, sanitized, message: "Only letters, spaces, hyphens and apostrophes are allowed." };
  }
  const lettersOnly = sanitized.replace(/[ '\-]/g, '');
  if (lettersOnly.length === 0) {
    return { valid: false, sanitized, message: 'Name must contain letters' };
  }
  return { valid: true, sanitized, message: 'OK' };
}

module.exports = { sanitizeName, isValidName, validateName };
